// =============================================================================
// ui/components/EduMark.tsx — the `?` beside a heading, and what it opens.
// -----------------------------------------------------------------------------
// Two tiers behind one glyph, matching the old client:
//
//   HOVER   a tooltip with one or two sentences
//   CLICK   a right-edge drawer with the full article
//
// ── ⚠ SELF-CONTAINED, WITH NO PROVIDER ──
//
// The old client hangs two listeners on `document` and keeps one tooltip element
// and one drawer element on `body` forever. The React equivalent would be a
// context provider at the app root — and every test that renders a tab in
// isolation would then have to know about it, which is how sixty tests died at
// once when the report gained two providers.
//
// So each mark owns its own tooltip and its own drawer. Only one can be open at
// a time in practice: the drawer's backdrop covers the page, so a second mark
// cannot be reached while the first is showing.
//
// ── ⚠ BOTH SURFACES GO THROUGH A PORTAL ──
//
// A mark sits deep inside a card. A `position: fixed` child normally escapes an
// ancestor's overflow, but NOT an ancestor with a transform — and this app has
// several. Portalling to `body` is what the old client's `appendChild` bought,
// and it costs one import.
//
// ── 🚨 THE ARTICLE RENDERS AS ELEMENTS, NEVER MARKUP ──
//
// `domain/eduMarkdown.ts` returns a tree, and each span becomes a React element.
// The old client builds an HTML string and sets it with `innerHTML`; that is safe
// only for as long as its escape is right. This client made the same call for the
// coach's model output (D113) and there is no reason to make a different one for
// prose that is going onto the same page.
//
// ── ⚠ THE TOOLTIP IS INTERACTIVE, AND THE HIDE IS DELAYED ──
//
// The cursor has to be able to travel from the 16px mark onto the tooltip without
// it vanishing on the way. A short timeout bridges the gap and is cancelled when
// the pointer lands on either. This is why the tooltip is a hover surface with
// pointer events rather than a `title` attribute.
// =============================================================================

import {
  useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  eduBlocks, eduEntry, type EduBlock, type EduSpan,
} from '../../selectors/education';

/** Long enough to cross the gap, short enough not to feel stuck. */
const HIDE_MS = 180;

export function EduMark({ eduKey }: { eduKey: string }) {
  const entry = eduEntry(eduKey);
  const [tip, setTip] = useState<{ left: number; top: number } | null>(null);
  const [open, setOpen] = useState(false);
  const markRef = useRef<HTMLSpanElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHide = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);

  const scheduleHide = useCallback(() => {
    cancelHide();
    timer.current = setTimeout(() => setTip(null), HIDE_MS);
  }, [cancelHide]);

  // ⚠ CLEARS ON UNMOUNT. The tab remounts on every navigation (D94), and a timer
  // that fires into a gone component is a console error on a screen the trader
  // has already left.
  useEffect(() => () => cancelHide(), [cancelHide]);

  const show = useCallback(() => {
    cancelHide();
    const r = markRef.current?.getBoundingClientRect();
    if (r) setTip({ left: r.left + r.width / 2, top: r.bottom + 8 });
  }, [cancelHide]);

  // ⚠ AN UNKNOWN KEY RENDERS NOTHING. The old client warns to the console and
  // emits an empty string; either way the mark is absent, and a `?` that opened
  // an empty drawer would be worse than no `?` at all. A test asserts every key
  // the components actually ask for resolves.
  if (!entry) return null;

  const hasArticle = !!entry.article;

  return (
    <>
      <span
        ref={markRef}
        className={`edu-mark${hasArticle ? ' clickable' : ''}`}
        role={hasArticle ? 'button' : 'note'}
        tabIndex={hasArticle ? 0 : -1}
        aria-label={`What is ${entry.title}?`}
        data-edu-key={eduKey}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
        onClick={() => { if (hasArticle) { setTip(null); setOpen(true); } }}
        onKeyDown={(e) => {
          if (hasArticle && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setTip(null);
            setOpen(true);
          }
        }}
      >?</span>

      {tip ? createPortal(
        <div
          className={`edu-tooltip${hasArticle ? ' clickable' : ''}`}
          style={{ left: tip.left, top: tip.top }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          onClick={() => { if (hasArticle) { setTip(null); setOpen(true); } }}
        >
          <div className="edu-tooltip-title">{entry.title}</div>
          <div className="edu-tooltip-body">{entry.tip}</div>
          {hasArticle ? (
            <div className="edu-tooltip-hint">click to read the full article →</div>
          ) : null}
        </div>,
        document.body,
      ) : null}

      {open && entry.article ? createPortal(
        <EduDrawer
          title={entry.title}
          article={entry.article}
          onClose={() => setOpen(false)}
        />,
        document.body,
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// The drawer
// ---------------------------------------------------------------------------

function EduDrawer(
  { title, article, onClose }: { title: string; article: string; onClose: () => void },
) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Escape closes, matching every other overlay in this client.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ⚠ FOCUS MOVES IN. The mark that opened this is behind the backdrop, so
  // leaving focus on it strands a keyboard user outside a modal dialog.
  useLayoutEffect(() => { closeRef.current?.focus(); }, []);

  return (
    <div className="edu-drawer-root open">
      <div className="edu-drawer-backdrop" onClick={onClose} />
      <aside className="edu-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="edu-drawer-header">
          <h2>{title}</h2>
          <button ref={closeRef} type="button" className="edu-drawer-close"
            onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="edu-drawer-body">
          <Article src={article} />
        </div>
      </aside>
    </div>
  );
}

/**
 * ⚠ THE ARTICLE'S OWN `# Title` IS DROPPED, not hidden with CSS. The drawer's
 * header already shows it, and the old client suppresses the duplicate with
 * `> h2:first-child { display: none }` — a rule that silently stops working the
 * moment a block is inserted above it.
 */
function Article({ src }: { src: string }) {
  const blocks = eduBlocks(src);
  const start = (blocks[0]?.kind === 'h' && blocks[0].level === 2) ? 1 : 0;
  return <>{blocks.slice(start).map((b, i) => <Block key={i} b={b} />)}</>;
}

function Block({ b }: { b: EduBlock }) {
  switch (b.kind) {
    case 'hr': return <hr />;
    case 'h': {
      const spans = <Spans spans={b.spans} />;
      if (b.level === 2) return <h2>{spans}</h2>;
      if (b.level === 3) return <h3>{spans}</h3>;
      return <h4>{spans}</h4>;
    }
    case 'p': return <p><Spans spans={b.spans} /></p>;
    case 'ul':
      return <ul>{b.items.map((it, i) => <li key={i}><Spans spans={it} /></li>)}</ul>;
    case 'ol':
      return <ol>{b.items.map((it, i) => <li key={i}><Spans spans={it} /></li>)}</ol>;
    case 'table':
      return (
        <table className="edu-table">
          <thead>
            <tr>{b.head.map((c, i) => <th key={i}><Spans spans={c} /></th>)}</tr>
          </thead>
          <tbody>
            {b.rows.map((r, i) => (
              <tr key={i}>{r.map((c, j) => <td key={j}><Spans spans={c} /></td>)}</tr>
            ))}
          </tbody>
        </table>
      );
    default: return null;
  }
}

function Spans({ spans }: { spans: EduSpan[] }) {
  return (
    <>
      {spans.map((s, i): ReactNode => {
        if (s.kind === 'strong') return <strong key={i}>{s.text}</strong>;
        if (s.kind === 'em') return <em key={i}>{s.text}</em>;
        if (s.kind === 'code') return <code key={i}>{s.text}</code>;
        if (s.kind === 'link') {
          return (
            <a key={i} href={s.href} target="_blank" rel="noopener noreferrer">
              {s.text}
            </a>
          );
        }
        return <span key={i}>{s.text}</span>;
      })}
    </>
  );
}
